import React, {useState} from 'react';
import {Alert, Box, Button, TextField, Typography} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {Panel} from '../../components/Panel/Panel';
import {
  UpdateCorrespondenceNeed,
  UpdateCorrespondenceNeedVariables,
} from '../../schema-types/UpdateCorrespondenceNeed';
import {getErrorMessage} from '../../utils/getErrorMessage';
import validateUUID from '../../utils/validateUUID';
import {UPDATE_CORRESPONDENCE_NEED} from './correspondenceNeedQueries';

export type CorrespondenceNeedModalContact = {id: string; simplifiedName: string | null};

/** The select value that stands for "no requirement" (NULL in Curo). */
export const NO_REQUIREMENT = '';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after Curo accepted the change, before the modal closes. */
  onSaved: () => void;
  accountId: string;
  contact: CorrespondenceNeedModalContact;
  /** The value Curo holds now; may be one OneView cannot set ("Other", "None"). */
  currentValue: string | null;
  /** The values OneView may set, from `correspondenceNeedOptions`. */
  options: string[];
};

/**
 * "Client Correspondence needs" dialog from the DBO-80 mockup: one single
 * choice (no requirement, Audio, Braille, Large Font), Cancel and Update.
 * Update writes to Curo in the same request, so a Curo rejection is shown here
 * and nothing is left half-saved.
 */
export const CorrespondenceNeedModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  accountId,
  contact,
  currentValue,
  options,
}) => {
  const currentIsSelectable = currentValue !== null && options.includes(currentValue);
  const [value, setValue] = useState(currentIsSelectable ? currentValue : NO_REQUIREMENT);
  const [errors, setErrors] = useState<string[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [updateCorrespondenceNeed, {loading}] = useMutation<
    UpdateCorrespondenceNeed,
    UpdateCorrespondenceNeedVariables
  >(UPDATE_CORRESPONDENCE_NEED);

  const unchanged = value === (currentValue ?? NO_REQUIREMENT);
  const canSubmit = !loading && !unchanged;

  const submit = async () => {
    setErrors([]);
    try {
      const {data} = await updateCorrespondenceNeed({
        variables: {
          contactId: validateUUID(contact.id)!,
          accountId: validateUUID(accountId)!,
          correspondenceNeed: value === NO_REQUIREMENT ? null : value,
        },
      });
      const result = data?.updateCorrespondenceNeed;
      if (!result?.ok) {
        setErrors(
          result?.errors?.length
            ? result.errors
            : ['Could not save the correspondence need.']
        );
        return;
      }
      enqueueSnackbar('Correspondence need updated in Curo.', {variant: 'success'});
      onSaved();
      onClose();
    } catch (error) {
      setErrors([getErrorMessage(error, 'Could not reach the server.')]);
    }
  };

  const contactName = contact.simplifiedName || 'contact';

  return (
    <ScrollableModal size="small" autoHeight open={open} onClose={onClose}>
      <Panel title={`Client Correspondence needs: ${contactName}`}>
        <TextField
          id="correspondence-need"
          select
          SelectProps={{native: true}}
          label="Correspondence need"
          fullWidth
          size="small"
          InputLabelProps={{shrink: true}}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        >
          <option value={NO_REQUIREMENT}>No requirement</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </TextField>
        {currentValue !== null && !currentIsSelectable ? (
          <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
            Curo currently holds &quot;{currentValue}&quot;, which OneView cannot set.
            Updating replaces it.
          </Typography>
        ) : null}
        {errors.length > 0 ? (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.join(' ')}
          </Alert>
        ) : null}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            Update
          </Button>
        </Box>
      </Panel>
    </ScrollableModal>
  );
};
